import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Table, Input, Alert, Checkbox, Popconfirm, notification } from "antd"
import { Link, useParams } from "react-router-dom"

import { byClientThenGroup, byGroupThenClient } from "./finalizeSort"

import { formatCurrency } from "../../../lib/utilities"
import {
    finalizeCodeKey,
    finalizeRun,
    selectFinalizeCodes,
    selectFinalizeErrors,
    selectFinalizePlan,
    selectFinalizing,
    setFinalizeCode,
} from "../clientBatchingSlice"

// Mirrors PreflightFindings' labels so the two screens name severities the same way.
const SEVERITY_LABELS = {
    will_fail: "will fail",
    will_be_wrong: "will be wrong",
    will_be_blank: "will be blank",
}

export default function FinalizeBatchTable() {
    const dispatch = useDispatch()
    const { runId } = useParams()
    const plan = useSelector(selectFinalizePlan)
    const codes = useSelector(selectFinalizeCodes)
    const errors = useSelector(selectFinalizeErrors)
    const finalizing = useSelector(selectFinalizing)

    const [ackDrift, setAckDrift] = useState(false)
    const [ackWillFail, setAckWillFail] = useState(false)

    if (!plan) return null

    const groups = plan.groups || []
    const drift = plan.drift || {}
    const driftCount = (drift.missing_bill_ids || []).length + (drift.already_batched_bill_ids || []).length
    const ungrouped = plan.ungrouped || []
    const clientChanged = plan.client_changed || []
    const orphaned = plan.orphaned_bill_ids || []
    const willFail = (plan.preflight && plan.preflight.will_fail) || []
    const preflightSummary = plan.preflight && plan.preflight.summary

    const errorFor = (record) =>
        errors.find(
            (e) => e.client_id === record.client_id && (e.batch_group_id || null) === (record.batch_group_id || null)
        )

    const allCodesPresent = groups.every((group) => (codes[finalizeCodeKey(group)] || "").trim().length > 0)

    // Hard stops have no acknowledgement: there is no correct thing to write for an ungrouped
    // division, a client this run no longer holds, or a bill with no division.
    const hardStop = ungrouped.length > 0 || clientChanged.length > 0 || orphaned.length > 0

    const canFinalize =
        !finalizing &&
        groups.length > 0 &&
        !hardStop &&
        allCodesPresent &&
        (willFail.length === 0 || ackWillFail) &&
        (driftCount === 0 || ackDrift)

    const handleFinalize = async () => {
        const response = await dispatch(
            finalizeRun(runId, { acknowledgeDrift: ackDrift, acknowledgeWillFail: ackWillFail })
        )
        if (response && response.error) {
            const data = response.data || {}
            notification.error({
                message: "Could not finalize this run",
                description: data.message ? data.message.join(" ") : "Please try again.",
            })
        } else if (response && response.data) {
            notification.success({ message: "Run finalized", description: response.data.message })
        }
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                {/* one wrapper: .card-body is a flex row */}
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Finalize</h2>
                            <div className="badge">{groups.length}</div>
                        </div>

                        <div className="actions">
                            <div className="table-action-links">
                                {canFinalize ? (
                                    <Popconfirm
                                        title={`Create ${groups.length} accounting batch(es) and attach the bills? This cannot be undone.`}
                                        okText="Finalize"
                                        cancelText="Cancel"
                                        onConfirm={handleFinalize}
                                    >
                                        <a href="javascript:;">{finalizing ? "Finalizing…" : "Finalize run"}</a>
                                    </Popconfirm>
                                ) : (
                                    <span className="disabled">Finalize run</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {hardStop && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message="This run cannot be finalized as it stands."
                            description={
                                <div>
                                    {ungrouped.map((row) => (
                                        <div key={`ug-${row.client_id}`}>
                                            {row.client_name}: {row.bill_ids.length} bill(s) are in a division that has
                                            lost its batch group, so there is no correct file format for them. Restore
                                            the batch group, or exclude those bills in Review.
                                        </div>
                                    ))}
                                    {clientChanged.map((row) => (
                                        <div key={`cc-${row.client_id}`}>
                                            {row.client_name}: this run no longer holds that client. Discard the run and
                                            sweep again.
                                        </div>
                                    ))}
                                    {orphaned.length > 0 && (
                                        <div>
                                            {orphaned.length} bill(s) no longer resolve to a division, so there is no
                                            batch to put them in.
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    )}

                    {willFail.length > 0 && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${willFail.length} client(s) have a problem that will stop their file being produced at all.`}
                            description={
                                <div>
                                    <ul>
                                        {willFail.map((f) => (
                                            <li key={`${f.client_id}-${f.key}`}>
                                                {f.client_name}: {f.message}
                                            </li>
                                        ))}
                                    </ul>
                                    <Checkbox checked={ackWillFail} onChange={(e) => setAckWillFail(e.target.checked)}>
                                        Finalize anyway. The batches will be created; those clients&apos; files have to
                                        be fixed and regenerated.
                                    </Checkbox>
                                    <div>
                                        <Link to={`/client_batching/runs/${runId}/preflight`}>
                                            Review pre-flight first
                                        </Link>
                                    </div>
                                </div>
                            }
                        />
                    )}

                    {driftCount > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${driftCount} bill(s) have changed since the sweep and will be dropped from this run.`}
                            description={
                                <div>
                                    {(drift.missing_bill_ids || []).length > 0 && (
                                        <div>{drift.missing_bill_ids.length} no longer exist.</div>
                                    )}
                                    {(drift.already_batched_bill_ids || []).length > 0 && (
                                        <div>
                                            {drift.already_batched_bill_ids.length} were batched by someone else. They
                                            stay in that batch; this run will not move them.
                                        </div>
                                    )}
                                    <Checkbox checked={ackDrift} onChange={(e) => setAckDrift(e.target.checked)}>
                                        Drop them and finalize the rest.
                                    </Checkbox>
                                </div>
                            }
                        />
                    )}

                    {/* Only will_fail gates the action, so without this a reviewer could finalize
                        without ever noticing a will_be_wrong -- e.g. bills that will be batched but
                        never appear in the client's file. */}
                    {preflightSummary && preflightSummary.total > 0 && willFail.length === 0 && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`Pre-flight found ${preflightSummary.total} issue(s) on this run, worst: ${
                                SEVERITY_LABELS[preflightSummary.worst_severity] || preflightSummary.worst_severity
                            }.`}
                            description={
                                <span>
                                    None of them stop a file being produced, so you can finalize.{" "}
                                    <Link to={`/client_batching/runs/${runId}/preflight`}>Review them first</Link>
                                </span>
                            }
                        />
                    )}

                    <p className="p-10">
                        One batch per client and batch group. Codes are pre-filled as client number, date and batch
                        group — for example 076-261023 PII 020, or 042-261023 for a client without batch groups — with
                        -1, -2 for any further batch of the same group that day. Edit any you need to.
                    </p>

                    {/* Pre-sorted as well, so the base order is Client then Batch Group whatever the API sends. */}
                    <Table dataSource={[...groups].sort(byClientThenGroup)} rowKey={(r) => finalizeCodeKey(r)} pagination={false}>
                        {/* Client then Batch Group by default; only these two columns sort. */}
                        <Table.Column
                            title="Client"
                            key="client_name"
                            dataIndex="client_name"
                            sorter={byClientThenGroup}
                            defaultSortOrder="ascend"
                            sortDirections={["ascend", "descend", "ascend"]}
                        />
                        <Table.Column
                            title="Batch Group"
                            key="batch_group_name"
                            dataIndex="batch_group_name"
                            sorter={byGroupThenClient}
                            sortDirections={["ascend", "descend", "ascend"]}
                            render={(text) => text || "No batch group"}
                        />
                        <Table.Column title="Bills" key="bill_count" dataIndex="bill_count" align="right" />
                        <Table.Column
                            title="Current Charges"
                            key="current_charges_sum"
                            dataIndex="current_charges_sum"
                            align="right"
                            render={(text) => formatCurrency(text)}
                        />
                        <Table.Column
                            title="Client Batch Cost"
                            key="cost_sum"
                            dataIndex="cost_sum"
                            align="right"
                            render={(text) => formatCurrency(text)}
                        />
                        <Table.Column
                            title="Batch Code"
                            key="batch_code"
                            width={280}
                            render={(text, record) => {
                                const key = finalizeCodeKey(record)
                                const error = errorFor(record)
                                return (
                                    <div>
                                        <Input
                                            value={codes[key] || ""}
                                            status={error ? "error" : ""}
                                            disabled={finalizing}
                                            onChange={(e) =>
                                                dispatch(setFinalizeCode({ key, value: e.target.value }))
                                            }
                                        />
                                        {record.batch_code_taken && !error && (
                                            <div style={{ color: "#d46b08", marginTop: "4px" }}>
                                                This client already has a batch today, so this continues that
                                                day&apos;s sequence.
                                            </div>
                                        )}
                                        {error && (
                                            <div style={{ color: "#cf1322", marginTop: "4px" }}>{error.message}</div>
                                        )}
                                    </div>
                                )
                            }}
                        />
                    </Table>
                </div>
            </div>
        </div>
    )
}
